// The project shell (Console D, apsio-planning/design/console-d): the sidebar (sticky, 244 px,
// a 64 px rail with `[`, a drawer on a phone), the 56 px bar with the scope, the query chips and
// Find, the one-line notices under it, and the page. The keyboard: ⌘K and / open Find, ? the
// keys, [ the rail, g and a letter go to a page.
import { useQuery } from '@tanstack/react-query'
import { Link, Outlet, useLocation, useNavigate, useSearch } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { billingQuery, meQuery, orgQuery, projectsQuery } from '../api/queries'
import type { Platform } from '../api/types'
import { type MessageId, useI18n } from '../i18n'
import { usePhoneBreakpoint } from '../lib/breakpoint'
import { type ErasingSubject, useErasing } from '../lib/erasing'
import { useFlash } from '../lib/flash'
import { shortcutOff } from '../lib/keys'
import { isSettingsKey, type NavKey, navKey } from '../lib/nav'
import { readRail, saveRail } from '../lib/rail'
import { useProjectParams } from '../routes/params'
import { carried, type ScopeSearch } from '../routes/search'
import { sandboxFor } from '../sandbox'
import { CommandBar, type CommandLayer } from './CommandBar'
import { PageHead, useToast } from './d'
import { PageHeadRow } from './d/Page'
import { ProjectDeletions, useProjectDeletions } from './ProjectDeletions'
import { Bar, ScopeFilterRow } from './shell/Bar'
import { PERF_PAGES, Sidebar, useLastConsolePage } from './shell/Sidebar'
import { ErasingNote } from './states'

export const PLATFORM_LABEL: Record<Platform, string> = {
  ios: 'iOS',
  android: 'Android',
  react_native: 'React Native',
}

/** An app's name with its platform, unless the name already says it. */
export function appLabel(app: { name: string; platform: Platform }): string {
  const platform = PLATFORM_LABEL[app.platform]
  return app.name.toLowerCase().includes(platform.toLowerCase())
    ? app.name
    : `${app.name} · ${platform}`
}

/** `g` then a letter: the pages that exist (Console D's keys). */
const GO: Record<string, string> = {
  h: 'home',
  n: 'agents',
  i: 'issues',
  r: 'releases',
  p: 'performance',
  u: 'users',
  s: 'sessions',
  l: 'logs',
  ',': 'settings/general',
}

export function Shell() {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const erasingNow = useErasing(orgId, projectId)
  // Toasts that outlive the page that asked (a project deleted or back, APS-401).
  const { toast, show } = useToast()
  useFlash(show)
  const navigate = useNavigate()
  const location = useLocation()
  const search = useSearch({ strict: false }) as ScopeSearch
  const sandbox = sandboxFor(projectId)
  const me = useQuery({ ...meQuery, retry: false, throwOnError: false })
  const billing = useQuery({ ...billingQuery(orgId), enabled: !sandbox })
  // Apsio's own flags for this organization (D58): the preview notice is the first they gate.
  const flags = useQuery({ ...orgQuery(orgId), enabled: !sandbox, throwOnError: false })
  const preview = !!flags.data?.product_flags.includes('console_preview')
  const [drawer, setDrawer] = useState(false)
  const [layer, setLayer] = useState<CommandLayer>(null)
  const base = `/o/${orgId}/p/${projectId}`
  const current = location.pathname.startsWith(base)
    ? navKey(location.pathname.slice(base.length))
    : undefined
  const settings = isSettingsKey(current)
  const backTo = useLastConsolePage(location.href, settings)
  const scope = carried(search)
  const org = me.data?.orgs.find((o) => o.id === orgId)

  // A new page closes the drawer.
  // biome-ignore lint/correctness/useExhaustiveDependencies: closes on every route change
  useEffect(() => setDrawer(false), [location.pathname])
  // So does crossing the phone breakpoint, which moves the sidebar without its slide (APS-376).
  usePhoneBreakpoint(useCallback(() => setDrawer(false), []))

  const [rail, setRail] = useState(readRail)
  const toggleRail = useCallback(() => {
    setRail((r) => {
      saveRail(!r)
      return !r
    })
  }, [])
  useEffect(() => {
    let g = 0
    const onKey = (e: KeyboardEvent) => {
      if (shortcutOff(e) || layer) return
      if (g && Date.now() - g < 900) {
        g = 0
        const to = GO[e.key]
        if (to) {
          e.preventDefault()
          navigate({ href: `${base}/${to}` })
        }
        return
      }
      if (e.key === 'g') g = Date.now()
      else if (e.key === '/') {
        e.preventDefault()
        setLayer('find')
      } else if (e.key === '[') {
        e.preventDefault()
        toggleRail()
      } else if (e.key === 'Escape' && drawer) setDrawer(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [base, navigate, toggleRail, layer, drawer])

  const plan = { orgId, projectId, section: 'plan' }
  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        {t('shell.skip')}
      </a>
      <Sidebar
        current={current}
        scope={scope}
        open={drawer}
        backTo={backTo}
        onKeys={() => setLayer('keys')}
      />
      {drawer ? (
        // biome-ignore lint/a11y/noStaticElementInteractions: the scrim closes the drawer; Esc does too
        // biome-ignore lint/a11y/useKeyWithClickEvents: Esc closes it (above)
        <div className="nav-scrim" onClick={() => setDrawer(false)} />
      ) : null}
      <div className="main">
        <Bar
          onMenu={() => setDrawer(!drawer)}
          onFind={() => setLayer('find')}
          onRail={toggleRail}
          drawerOpen={drawer}
          rail={rail}
          erasing={!!erasingNow}
        />
        {sandbox ? (
          <section className="shell-notice" aria-label={t('sandbox.bannerLabel')}>
            <span>
              <b>{t('sandbox.banner')}</b> <span className="dim">{t('sandbox.bannerBody')}</span>
            </span>
          </section>
        ) : org?.status !== 'payment_failed' && billing.data?.switch?.offered ? (
          // A card issued in Brazil on a USD subscription (D88 addendum): Stripe charges it in
          // reais only, so the plan settings offer the switch.
          <p role="status" className="shell-notice error billing-banner">
            <span>
              {t(
                billing.data.switch.past_due
                  ? 'banner.brazilianCardPastDue'
                  : 'banner.brazilianCard',
              )}
            </span>
            <Link to="/o/$orgId/p/$projectId/settings/$section" params={plan}>
              {t('banner.switchToReais')}
            </Link>
          </p>
        ) : org?.status === 'payment_failed' ||
          billing.data?.subscription?.status === 'past_due' ? (
          <p
            role={org?.status === 'payment_failed' ? 'alert' : 'status'}
            className="shell-notice error billing-banner"
          >
            <span>
              {t(org?.status === 'payment_failed' ? 'banner.paymentFailed' : 'banner.pastDue')}
            </span>
            <Link to="/o/$orgId/p/$projectId/settings/$section" params={plan}>
              {t('banner.openPlan')}
            </Link>
          </p>
        ) : preview ? (
          // The console_preview product flag (APS-469): off unless Apsio turns it on for the
          // organization in the admin console.
          <section className="shell-notice" aria-label={t('banner.previewLabel')}>
            <span>
              <b>{t('banner.preview')}</b> <span className="dim">{t('banner.previewBody')}</span>
            </span>
          </section>
        ) : null}
        {/* Being deleted (APS-406): one calm page, not the same state in every panel. */}
        {erasingNow && current && !settings ? (
          <ErasingPage subject={erasingNow} current={current} />
        ) : (
          // On a narrow window the bar's filters sit under each page head (APS-228).
          <PageHeadRow.Provider value={<ScopeFilterRow />}>
            <Outlet />
          </PageHeadRow.Provider>
        )}
        {toast}
      </div>
      <CommandBar layer={layer} setLayer={setLayer} onRail={toggleRail} />
    </div>
  )
}

/** Each page's title, by its sidebar key: the head the page keeps while it is being deleted. */
const TITLES: Partial<Record<NavKey, MessageId>> = {
  home: 'nav.home',
  agents: 'nav.agents',
  instrumentation: 'nav.instrumentation',
  signals: 'nav.signals',
  alerts: 'nav.alerts',
  issues: 'nav.issues',
  releases: 'nav.releases',
  health: 'nav.health',
  perf: 'nav.performance',
  users: 'nav.people',
  sessions: 'nav.sessions',
  journeys: 'nav.journeys',
  flags: 'flags.title',
  explore: 'nav.explore',
  logs: 'nav.logs',
  dashboards: 'nav.dashboards',
  setup: 'setup.title',
  ...Object.fromEntries(PERF_PAGES.map((p) => [p.key, p.label])),
}

/**
 * The project, or its organization, is being deleted (APS-406): the page keeps its own head (its
 * title, the project) and says so under it, once, instead of in every panel. A project's own
 * deletion (APS-401) shows its date there too, and owners and admins cancel it from there.
 */
function ErasingPage({ subject, current }: { subject: ErasingSubject; current: NavKey }) {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const projects = useQuery(projectsQuery(orgId))
  const me = useQuery({ ...meQuery, retry: false, throwOnError: false })
  const role = me.data?.orgs.find((o) => o.id === orgId)?.role
  // Every member sees when it runs; owners and admins cancel it (its one action here).
  const deletions = useProjectDeletions(orgId, subject === 'project' && !!role)
  const own = deletions.data?.erasures.find((e) => e.project_id === projectId)
  // Hidden from the organization's lists meanwhile: its name comes with its deletion.
  const name = projects.data?.find((p) => p.id === projectId)?.name ?? own?.project_name
  const title = TITLES[current]
  return (
    <main id="main" className="pg">
      <PageHead title={title ? t(title) : name} meta={title ? name : undefined} />
      <ErasingNote subject={subject}>
        {subject === 'project' && own ? (
          <ProjectDeletions
            orgId={orgId}
            erasures={[own]}
            canCancel={role !== 'member'}
            buttonClass="btn"
          />
        ) : null}
      </ErasingNote>
    </main>
  )
}
